/**
 * Toast — Notifications globales (succès, erreur, info, saved)
 * Utilise un event bus simple pour déclencher des toasts depuis n'importe où.
 *
 * Position: top-right (overlay). Type "saved" dure 1s et est vert (synapse-olive).
 */

import { useState, useEffect } from "react";

export type ToastType = "success" | "error" | "info" | "saved";

export interface ToastMessage {
  id: number;
  type: ToastType;
  label: string;
  message: string;
  /** Durée d'affichage en ms — défaut 3000. "saved" → 1000. */
  duration?: number;
}

type ToastListener = (toast: ToastMessage) => void;

let toastId = 0;
let listeners: ToastListener[] = [];

export function showToast(
  type: ToastType,
  label: string,
  message: string,
  duration?: number,
) {
  const toast: ToastMessage = {
    id: ++toastId,
    type,
    label,
    message,
    duration: duration ?? (type === "saved" ? 1000 : 3000),
  };
  listeners.forEach((fn) => fn(toast));
}

const TOAST_STYLES: Record<ToastType, { border: string; labelColor: string; icon: string }> = {
  success: { border: "rgba(94,199,168,0.3)", labelColor: "var(--olive)", icon: "✓" },
  saved: { border: "rgba(94,199,168,0.45)", labelColor: "var(--olive)", icon: "✓" },
  error: { border: "rgba(207,107,98,0.3)", labelColor: "var(--rust)", icon: "⚠" },
  info: { border: "rgba(107,141,214,0.3)", labelColor: "var(--blue)", icon: "ℹ" },
};

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const listener: ToastListener = (toast) => {
      setToasts((prev) => [...prev, toast]);
      const ttl = toast.duration ?? (toast.type === "saved" ? 1000 : 3000);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, ttl);
    };
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((fn) => fn !== listener);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: "70px",
        right: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        zIndex: 9999,
        pointerEvents: "none",
        maxWidth: "360px",
      }}
    >
      {toasts.map((toast) => {
        const style = TOAST_STYLES[toast.type];
        return (
          <div
            key={toast.id}
            className={`toast show toast-${toast.type}`}
            style={{
              borderColor: style.border,
              background:
                toast.type === "saved"
                  ? "rgba(94,199,168,0.14)"
                  : "rgba(18,22,41,0.98)",
              color: "var(--text)",
              fontFamily: "var(--sans)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
              padding: "12px 16px",
              borderRadius: "12px",
              border: `1px solid ${style.border}`,
              fontSize: "13px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              pointerEvents: "auto",
              animation: toast.type === "saved"
                ? "toast-saved 1s ease-out forwards"
                : "toast-in 0.25s ease-out forwards",
            }}
          >
            <span
              className="toast-icon"
              style={{ color: style.labelColor, fontWeight: 600 }}
            >
              {style.icon} {toast.label}
            </span>
            <span style={{ color: "var(--muted)", fontSize: "12px" }}>
              {toast.message}
            </span>
          </div>
        );
      })}
      <style>{`
        @keyframes toast-in {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes toast-saved {
          0%   { transform: translateX(40px); opacity: 0; }
          10%  { transform: translateX(0);    opacity: 1; }
          80%  { transform: translateX(0);    opacity: 1; }
          100% { transform: translateX(0);    opacity: 0; }
        }
      `}</style>
    </div>
  );
}